import { useEffect, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Search,
  RefreshCcw,
  Printer,
  FileSpreadsheet,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const monthList = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

const PaymentAbstractAllSites = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const [branches, setBranches] = useState([]);
  const [grades, setGrades] = useState([]);
  const [years, setYears] = useState([]);

  const [pfnon, setPfnon] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [payment, setPayment] = useState("Select");
  const [grade, setGrade] = useState("Select");
  const [year, setYear] = useState("");

  const [selectedMonths, setSelectedMonths] = useState([]); // 🔥 store numbers

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white"
      : "bg-white border-gray-200 text-gray-900");

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg transition ${primaryBtnClass} ${primaryHoverClass}`;

  /* ================= META ================= */
  const loadMeta = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/payment-abstract/meta`, {
        params: { finYearId, branchId, branchType },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setGrades(res.data.grades || []);
        setYears(res.data.years || []);
        setYear(res.data.defaultYear || "");

        // default current month
        const currentMonth = new Date().getMonth() + 1;
        setSelectedMonths([currentMonth]);
      }
    } catch {
      toast.error("Failed to load meta");
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  /* ================= FETCH DATA ================= */
  const fetchData = async (p = 1) => {
    if (!year) {
      toast.warn("Please select year");
      return;
    }

    if (selectedMonths.length === 0) {
      toast.warn("Please select at least one month");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/payment-abstract/data`, {
        params: {
          finYearId,
          branchId,
          branchType,
          branch,
          pfnon,
          payment,
          grade,
          months: selectedMonths.join(","), // 🔥 numeric CSV
          year,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotals(res.data.totals || {});
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page > 1) fetchData(page);
  }, [page]);

  /* ================= VIEW ALL ================= */
  const handleViewAll = () => {
    setPfnon("Select");
    setBranch("Select");
    setPayment("Select");
    setGrade("Select");
    fetchData(1);
  };

  /* ================= TOGGLE MONTH ================= */
  const toggleMonth = (id) => {
    setSelectedMonths((prev) =>
      prev.includes(id)
        ? prev.filter((m) => m !== id)
        : [...prev, id]
    );
  };

  /* ================= PRINT ================= */
  const handlePrint = () => {
    const qs = new URLSearchParams({
      finYearId,
      branchId,
      branchType,
      branch,
      pfnon,
      payment,
      grade,
      months: selectedMonths.join(","),
      year,
    });

    window.open(`/PayRoll/PaymentAbstractReport_Print?${qs}`, "_blank");
  };

  /* ================= EXCEL ================= */
  const handleExcel = () => {
    const qs = new URLSearchParams({
      finYearId,
      branchId,
      branchType,
      branch,
      pfnon,
      payment,
      grade,
      months: selectedMonths.join(","),
      year,
    });

    window.open(`${BASE_URL}/api/payment-abstract/excel?${qs}`);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title="Payment Abstract List"
          setSkin={setSkin}
          currentSkin={skin}
        />

        {/* FILTER CARD */}
        <div className={`${cardBase} p-6 mt-6`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

            <select value={pfnon} onChange={(e) => setPfnon(e.target.value)} className={inputBase}>
              <option value="Select">Select PF/NonPF</option>
              <option value="Yes">PF</option>
              <option value="No">Non PF</option>
            </select>

            <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
              <option value="Select">Select Branch</option>
              {branches.map((b) => (
                <option key={b.fld_branchid} value={b.fld_branchid}>
                  {b.fld_branchname}
                </option>
              ))}
            </select>

            <select value={payment} onChange={(e) => setPayment(e.target.value)} className={inputBase}>
              <option value="Select">Select Payment</option>
              <option value="1">Cash</option>
              <option value="2">Bank</option>
            </select>

            <select value={grade} onChange={(e) => setGrade(e.target.value)} className={inputBase}>
              <option value="Select">Select Grade</option>
              {grades.map((g) => (
                <option key={g.fld_gradeid} value={g.fld_gradeid}>
                  {g.fld_gradename}
                </option>
              ))}
            </select>

            <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
              <option value="">Select Year</option>
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* MONTHS */}
          <div className="mt-6">
            <div className="font-bold mb-2">Select Month(s)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {monthList.map((m) => (
                <label key={m.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedMonths.includes(m.id)}
                    onChange={() => toggleMonth(m.id)}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => fetchData(1)} className={primaryBtn}>
              <Search size={16} /> Search
            </button>

            <button onClick={handleViewAll} className={primaryBtn}>
              <RefreshCcw size={16} /> View All
            </button>

            <button onClick={handlePrint} className={primaryBtn}>
              <Printer size={16} /> Print
            </button>

            <button onClick={handleExcel} className={primaryBtn}>
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className={`${cardBase} mt-6 overflow-x-auto`}>
          <table className="w-full text-sm text-center">
            <thead className={`${primaryBtnClass} text-white`}>
              <tr>
                <th className="p-3">S.No</th>
                <th>Department</th>
                <th>Gross Salary</th>
                <th>PF</th>
                <th>ESI</th>
                <th>Total Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6">
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx} className="border-b">
                    <td>{(page - 1) * pageSize + idx + 1}</td>
                    <td>{r.fld_departmentname}</td>
                    <td>{Number(r.grosspay).toFixed(2)}</td>
                    <td>{Number(r.pf).toFixed(2)}</td>
                    <td>{Number(r.esi).toFixed(2)}</td>
                    <td>{Number(r.netpay).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>

            {rows.length > 0 && (
              <tfoot className="font-bold bg-gray-200 dark:bg-gray-700">
                <tr>
                  <td></td>
                  <td>Total</td>
                  <td>{totals.grosspay?.toFixed(2)}</td>
                  <td>{totals.pf?.toFixed(2)}</td>
                  <td>{totals.esi?.toFixed(2)}</td>
                  <td>{totals.netpay?.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div>
              Showing {showingFrom} to {showingTo} of {total}
            </div>

            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} onClick={() => setPage(i + 1)}>
                  {i + 1}
                </button>
              ))}
              <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PaymentAbstractAllSites;
